import {
  GAME_DATA,
  type Gather,
  type InventoryResponse,
  type ItemCounts,
  type Landed,
  type PublicTile,
  type PublicUser,
  type SettleResponse,
} from '@heartpatch/shared';
import { createAwayTracker } from '../factory/away.js';
import { factoryApi, type FactoryApi } from '../factory/factory-api.js';
import { createFactoryPanel } from '../factory/factory-panel.js';
import { createFactoryStrip } from '../factory/factory-strip.js';
import {
  batchRows,
  FACTORY_TEXT,
  showsWelcomeBack,
  welcomeChips,
} from '../factory/factory-view.js';
import { recipeBookApi } from '../recipes/recipe-book-api.js';
import { ApiRequestError } from '../net/api.js';
import type { TileActions } from '../map/map-screen.js';
import { newIdempotencyKey } from '../net/idempotency-key.js';
import { hpClose } from '../ui/button/button.js';
import { el, messageOf } from '../ui/dom.js';
import {
  bagCrafts,
  bagItems,
  bagRecipes,
  describeItems,
  gatherChip,
  itemName,
} from './bag-view.js';
import {
  AWAY_TEXT,
  onScreen,
  summaryCount,
  summaryEntries,
  summarySentence,
  type ScreenPoint,
  type SummaryEntry,
  type TrainedSquishy,
} from './away-summary.js';
import type { Portraits } from '../procedural/portraits.js';
import { createAwaySummary, type AwaySummaryDebug } from './away-summary-view.js';
import type { BuildSource } from '../building/tile-build.js';
import { CLEAR_CARD_ID, CLEAR_TEXT, clearCards, clearedNote } from './clear-model.js';
import { producedLines } from '../resource-buildings/resource-model.js';
import { formatTimeLeft, GameClock } from './game-clock.js';
import { inventoryApi, type InventoryApi } from './inventory-api.js';
import { itemDetail } from './item-detail.js';
import { itemIcon, itemIconNode } from './item-icons.js';
import { COMMAND_RETRY_MS, sendCommand } from './send-command.js';
import { tileAction, type TileAction } from './tile-action.js';
import './inventory.css';

// The bag and gathering (#17, design doc §12): a Bag entry in the My Home tray that
// opens a sheet of items (big numbers, pictures) and recipes, and the
// Gather button inside the map's tile panel. Timers are the server's
// timestamps; the screen only counts down to them on the game clock and asks
// the server to do everything (CLAUDE.md rule 1). Finished crafts, gathers
// and gatherers' cycles go straight into the bag (owner decision 2026-10-06):
// the screen asks the server to settle when a map opens, when the app comes
// back, and when the server says the next thing is due, and pops up what landed.

export interface InventoryScreenOptions {
  root: HTMLElement;
  /** Where the entry button goes (a tray over the map, ui/trays); defaults to `root`. */
  entryRoot?: HTMLElement;
  api?: InventoryApi;
  /** Device wall clock in ms (tests pass a fake). */
  now?: () => number;
  /** Dev builds show a "get stuff" button (server `HP_DEV_SQUISHY_GRANTS`). */
  devTools?: boolean;
  /** Something new landed in the bag (a gather or craft finished): the recipe book may open a page. */
  onCollected?: () => void;
  /**
   * A settle finished on this patch: it may have charged the morning's
   * upkeep or paused a building (#601), so the 💛 balance reads again.
   */
  onSettled?: () => void;
  /** The Crafting Factory's calls (#294). */
  factoryApi?: FactoryApi;
  /** The recipe book pages this account has opened (only those can be queued). */
  openPages?: () => Promise<readonly string[]>;
  /** Where "when did I last see this patch" is kept, for the welcome-back card (null: nowhere). */
  storage?: Storage | null;
  /** "See Factory" on the welcome-back card: the Factory's batches sheet (wherever it stands, #295). */
  onSeeFactory?: () => void;
  /** The Lorebook's way in (#307): a tile at the top of the sheet and a sparkle on the Bag button. */
  lore?: { readonly tile: HTMLElement; readonly badge: HTMLElement };
  /** The cellar key's treasure maps while a hunt is on (#306): under the Lorebook's tile. */
  keyMaps?: HTMLElement;
  /** The Bag opened (the Lorebook looks for new pages). */
  onBagOpen?: () => void;
  /** A food's "Care: Feed" chip: care with that food picked (the Bag closes first). */
  onFeed?: (itemId: string) => void;
  /**
   * A reply set the game clock (`gameNow`) to the server's time (#583): what
   * reads it (the map's season, the music) looks again.
   */
  onClockSync?: () => void;
  /**
   * Where things land on screen (#419): a tile's box, and my home base's,
   * while the map shows (null otherwise). What landed flies from there.
   */
  locate?: {
    readonly tile: (h: { q: number; r: number }) => ScreenBox | null;
    readonly home: () => ScreenBox | null;
    /** The tile a building stands on (the Factory, on a homestead since #461). */
    readonly building?: (id: string) => { q: number; r: number } | null;
  };
  /** What the "what landed" banner keeps below at the top of the screen (#419). */
  clearOf?: () => Iterable<Element>;
  /** The squishies' real pictures (#375), for the trained ones in the banner and its card (#465). */
  portraits?: Portraits;
}

/** Something that landed from a tile: a finished gather, land that finished clearing, or a Factory batch. */
type LandedFrom = Pick<Gather, 'q' | 'r' | 'items'>;

/** A box on screen in CSS pixels (the map's `ScreenRect`). */
interface ScreenBox {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Read-only state for the dev hook (Playwright asserts on it, not on pixels). */
export interface InventoryDebug {
  readonly mapId: string;
  readonly items: ItemCounts;
  readonly gathers: number;
  readonly crafts: number;
  readonly seasons: readonly string[];
  readonly bagOpen: boolean;
  /** The tile panel's action right now, if it's showing one of your nodes. */
  readonly tileAction: TileAction['kind'] | null;
  /** The gathering chip over the map: null while hidden; `ready` for the moment before it lands. */
  readonly chip: 'waiting' | 'ready' | null;
  /** What just landed, in words, while its banner shows (#419), else null. */
  readonly toast: string | null;
  /** The banner and its card (#419). */
  readonly summary: AwaySummaryDebug;
  /** Things landed since the Bag was last opened (the Home tab's news), 0 for none. */
  readonly bagNew: number;
  /** Settles so far that put something in the bag. */
  readonly landings: number;
  /** Chips of the last landing that flew from their own tile, not home base (#419). */
  readonly fromTiles: number;
  /** Crafting Factory batches going (#294), null with no Factory. */
  readonly batches: number | null;
  /** The Factory sheet is open, and what its panel shows. */
  readonly factorySheet: 'list' | 'pick' | 'stop' | null;
  /** The welcome-back card is showing. */
  readonly welcome: boolean;
}

export interface InventoryScreen {
  /** The map on screen (null: none): shows the Bag button and loads the bag. */
  setMap: (mapId: string | null) => Promise<void>;
  setUser: (user: PublicUser | null) => void;
  /** Shuts the bag, if it's open (the treasure map's "Show me on the map", #306). */
  closeBag: () => void;
  readonly tileActions: TileActions;
  /** The Build sheet's Clear land card on my homesteads (#242). */
  readonly clearSource: BuildSource;
  /** The bag on screen now (items, crafts, seasons), or null. */
  readonly bag: InventoryResponse | null;
  /** Settles the map on screen (banks what finished, owner decision 2026-10-06) and reads the bag again. */
  refresh: () => Promise<void>;
  /**
   * Starts a craft for the map on screen (the recipe book's "Make it"): the
   * server checks and takes the items (CLAUDE.md rule 1). Resolves to null
   * once started, or to a kid-readable line saying why not.
   */
  craft: (recipeId: string) => Promise<string | null>;
  /**
   * Starts a Crafting Factory batch (#294) of up to `count` (the server caps
   * it at what the bag can pay for). Null once started, or a line saying why not.
   */
  queue: (recipeId: string, count: number) => Promise<string | null>;
  /** Stops a Factory batch: what's made is kept, the rest comes back. */
  stopBatch: (batchId: string) => Promise<string | null>;
  /** Opens the Factory sheet; with a recipe, "How many?" for it. */
  openFactory: (recipeId?: string) => void;
  /** The game clock now, in ms (the server's). */
  gameNow: () => number;
  /**
   * Reads the server's time for a map's bag (#583), so a map about to be
   * built is dressed on the game clock. Never rejects: on a failure the clock
   * stays as it was.
   */
  syncClock: (mapId: string) => Promise<void>;
  /** Is this recipe book page open, as last read (the Factory only queues open pages)? */
  isPageOpen: (pageKey: string) => boolean;
  /** Ms until an ISO time on the server's clock (0 once it's passed), for countdowns. */
  msUntil: (iso: string) => number;
  readonly debug: InventoryDebug | null;
}

// Player-facing text (style guide §6, §9).
const TEXT = {
  bag: 'Bag',
  title: 'Your bag',
  close: 'Close',
  empty: 'Your bag is empty. Tap a spot on your land to gather!',
  makeTitle: 'Make things',
  makingTitle: 'Cooking',
  gatherTitle: 'Gathering',
  gather: 'Gather',
  make: 'Make',
  waiting: (left: string) => `Gathering… ready in ${left}`,
  making: (name: string, left: string) => `Making ${name}… ${left}`,
  busy: (left: string) => `Someone's gathering here. Ready in ${left}.`,
  busyDone: "Someone's gathering here.",
  gives: (what: string) => `Gives ${what}`,
  got: (what: string) => `Yay! ${what}`,
  started: 'Off you go! It pops into your bag when it’s ready.',
  keepsGoing: 'It keeps going while you play!',
  landing: 'Ready! Into your bag it goes…',
  readyNow: 'Ready!',
  chipWaiting: (what: string, left: string) => `Gathering ${what}… ready in ${left}`,
  chipReady: (what: string) => `${what} is ready!`,
  chipMore: (n: number) => ` (+${String(n)})`,
  craftBusy: 'Your pot is busy cooking!',
  justASec: 'Just a sec…',
  noMap: 'Visit a patch first!',
  devGrant: 'Get stuff (dev)',
  devFactory: 'Finish batches (dev)',
  findIt: 'Find it:',
} as const;

/**
 * Away this long, the banner says "Welcome back!" (shorter: "In your Bag!").
 * Only the tag's words: shorter than the Factory card's 30 minutes
 * (`FACTORY_RULES.welcomeBackMinutes`, a whole card that waits for a tap).
 */
const WELCOME_BACK_MS = 5 * 60_000; // TUNE: a quick look at another app isn't "away"
/**
 * How long a landing waits for its trained squishies' pictures (#465): one
 * drawn before shows at once; a new one that takes longer shows 🎯 this time
 * (and is ready the next). The banner never changes size once it's up.
 */
const PORTRAIT_WAIT_MS = 600; // TUNE
/** The public kinds; a secret one I befriended has no picture here and shows 🎯. */
const SPECIES = new Map(GAME_DATA.species.map((s) => [s.id, s]));
/** A settle asks a moment after the server's `nextAt`, so it has surely finished by the server's clock. */
const SETTLE_SLACK_MS = 400; // TUNE: covers the clock sync's rounding and a slow request
/** A settle that failed (a flaky phone connection, a rate limit) tries once more after this long. */
const SETTLE_RETRY_MS = 5000; // TUNE: long enough for a radio to come back

/** Where the welcome-back card remembers when I last saw a patch (#294), per account and patch. */
const SEEN_KEY = (userId: string, mapId: string) => `heartpatch.factory.seen.${userId}.${mapId}`;

/** Things a dev build hands out to try crafting without waiting. */
const DEV_ITEMS: ItemCounts = {
  timber: 10,
  stone: 10,
  emberwood: 10,
  treats: 10,
  pumpkins: 6,
  'witch-dust': 3,
};

export function createInventoryScreen(options: InventoryScreenOptions): InventoryScreen {
  const api = options.api ?? inventoryApi;
  const fapi = options.factoryApi ?? factoryApi;
  const clock = new GameClock(options.now);
  const device = options.now ?? (() => Date.now());
  const storage = options.storage === undefined ? safeStorage() : options.storage;
  const readPages = options.openPages ?? (() => recipeBookApi.get().then((res) => res.unlocked));
  /** Recipe book pages this account has opened, as last read. */
  let openPages = new Set<string>();
  /** How long the kid was away from a patch, for the welcome-back card (#294). */
  const presence = createAwayTracker({
    storage,
    now: device,
    key: (id) => (user ? SEEN_KEY(user.id, id) : null),
  });

  let user: PublicUser | null = null;
  let mapId: string | null = null;
  let state: InventoryResponse | null = null;
  /** Bumped by every map change, so a late reply can't land on another map. */
  let generation = 0;
  let working = false;
  let ticker: number | undefined;
  let panel: { container: HTMLElement; tile: PublicTile } | null = null;
  let shownAction: TileAction | null = null;
  let shownChip: InventoryDebug['chip'] = null;
  /** The one settle waiting for the next thing to finish (the server's `nextAt`). */
  let settleTimer: number | undefined;
  let landings = 0;
  /** Things landed since the Bag was last opened: the Bag button's "+N" and the Home tab's news. */
  let bagNew = 0;
  /**
   * The patch `bagNew` is about. A detour off the map (home base, a battle,
   * the wardrobe) and back keeps the news; another patch or player resets it.
   */
  let newFor: string | null = null;

  // ── Bag button and sheet ──────────────────────────────────────────────
  const open = el(
    'button',
    { type: 'button', class: 'bag-open', 'data-testid': 'bag-open', 'aria-label': TEXT.bag },
    el('span', { class: 'bag-open-icon', 'aria-hidden': 'true' }, '🎒'),
    el('span', { class: 'bag-open-label' }, TEXT.bag),
    ...(options.lore ? [options.lore.badge] : []),
  );
  // What landed since the Bag was last opened (#419): "+14" on the Bag button,
  // and one piece of news on the My Home tab (trays.ts reads `data-tray-alert`).
  const newPill = el('span', { class: 'bag-new', 'aria-hidden': 'true', 'data-testid': 'bag-new' });
  const newAlert = el('span', {
    class: 'bag-new-alert',
    'aria-hidden': 'true',
    'data-tray-alert': AWAY_TEXT.bagNews,
  });
  open.append(newPill, newAlert);
  const showBagNew = () => {
    newPill.textContent = bagNew > 0 ? `+${String(bagNew)}` : '';
    newAlert.textContent = bagNew > 0 ? '1' : '';
  };
  open.hidden = true;

  // The gathering chip: a gather takes minutes on a patch, so the map keeps
  // saying so until it lands in the bag, then it's gone. A tap opens the Bag.
  const chip = el('button', {
    type: 'button',
    class: 'gather-chip',
    'data-testid': 'gather-chip',
  });
  chip.hidden = true;

  const close = hpClose(TEXT.close);
  const note = el('p', { class: 'bag-note', role: 'status', 'data-testid': 'bag-note' });
  const itemsBox = el('ul', { class: 'bag-items', 'data-testid': 'bag-items' });
  // What a tapped item is for (#241): one card under the grid; tap again to close.
  const detailBox = el('div', { class: 'bag-detail', 'data-testid': 'bag-detail' });
  detailBox.hidden = true;
  let picked: string | null = null;
  const gathersTitle = el('h3', { class: 'bag-section-title' }, TEXT.gatherTitle);
  const gathersBox = el('ul', { class: 'bag-rows', 'data-testid': 'bag-gathers' });
  // The Crafting Factory's batches (#294): a strip under the items; a tap opens its sheet.
  const factoryStrip = createFactoryStrip(
    () => clock.now(),
    () => {
      openFactory();
    },
  );
  const craftsTitle = el('h3', { class: 'bag-section-title' }, TEXT.makingTitle);
  const craftsBox = el('ul', { class: 'bag-rows', 'data-testid': 'bag-crafts' });
  const recipesBox = el('ul', { class: 'bag-rows', 'data-testid': 'bag-recipes' });
  const sheet = el(
    'section',
    { class: 'bag', 'data-testid': 'bag', role: 'dialog', 'aria-labelledby': 'bag-title' },
    el('div', { class: 'tile-panel-head' }, el('h2', { id: 'bag-title' }, TEXT.title), close),
    ...(options.lore ? [options.lore.tile] : []),
    ...(options.keyMaps ? [options.keyMaps] : []),
    note,
    itemsBox,
    detailBox,
    factoryStrip.element,
    craftsTitle,
    craftsBox,
    gathersTitle,
    gathersBox,
    el('h3', { class: 'bag-section-title' }, TEXT.makeTitle),
    recipesBox,
  );
  sheet.hidden = true;

  // The Factory sheet (#294): the queue panel over the map or the Bag.
  const factoryPanel = createFactoryPanel({
    now: () => clock.now(),
    start: (recipeId, count) => startBatch(recipeId, count),
    stop: (batchId) => stopBatch(batchId),
    isOpen: (key) => openPages.has(key),
  });
  const factoryClose = hpClose(TEXT.close, 'factory-close');
  const factorySheet = el(
    'section',
    {
      class: 'bag factory-sheet',
      'data-testid': 'factory-sheet',
      role: 'dialog',
      'aria-labelledby': 'factory-title',
    },
    el(
      'div',
      { class: 'tile-panel-head' },
      el('h2', { id: 'factory-title' }, `🏭 ${FACTORY_TEXT.name}`),
      factoryClose,
    ),
    factoryPanel.element,
  );
  factorySheet.hidden = true;
  factoryClose.addEventListener('click', () => {
    factorySheet.hidden = true;
    factoryPanel.pause();
    render();
  });

  // Welcome back (#294, owner decision 2026-10-08): after a while away, what
  // the Factory made, in one card. Everything is in the bag already.
  const welcomeGot = el('div', {
    class: 'factory-welcome-got',
    'data-testid': 'factory-welcome-got',
  });
  const welcomeStill = el('ul', { class: 'factory-stop-lines' });
  const welcomeOk = el(
    'button',
    { type: 'button', class: 'hp-button hp-button-primary', 'data-testid': 'factory-welcome-ok' },
    FACTORY_TEXT.ok,
  );
  const welcomeSee = el(
    'button',
    {
      type: 'button',
      class: 'hp-button hp-button-secondary',
      'data-testid': 'factory-welcome-see',
    },
    FACTORY_TEXT.seeFactory,
  );
  const welcome = el(
    'div',
    { class: 'factory-welcome', 'data-testid': 'factory-welcome' },
    el(
      'section',
      { class: 'factory-welcome-card', role: 'dialog', 'aria-labelledby': 'factory-welcome-title' },
      el('div', { 'aria-hidden': 'true', style: 'font-size:44px;line-height:1' }, '🏭'),
      el('h2', { id: 'factory-welcome-title' }, FACTORY_TEXT.welcomeTitle),
      el('p', { class: 'factory-about' }, FACTORY_TEXT.welcomeLine),
      welcomeGot,
      welcomeStill,
      el('div', { class: 'factory-row' }, welcomeOk, ...(options.onSeeFactory ? [welcomeSee] : [])),
    ),
  );
  welcome.hidden = true;
  welcomeOk.addEventListener('click', () => {
    welcome.hidden = true;
  });
  welcomeSee.addEventListener('click', () => {
    welcome.hidden = true;
    options.onSeeFactory?.();
  });

  (options.entryRoot ?? options.root).append(open);
  options.root.append(chip, sheet, factorySheet, welcome);
  // What just landed (#419): a slim banner of chips, wherever the player is
  // looking (the book included), and a card with everything.
  const summary = createAwaySummary(
    options.root,
    options.clearOf ? { clearOf: options.clearOf } : {},
  );

  if (options.devTools) {
    const dev = el(
      'button',
      { type: 'button', class: 'hp-button hp-button-secondary hp-button-small' },
      TEXT.devGrant,
    );
    dev.addEventListener('click', () => {
      void act(async (id) => {
        const items = await api.dev.grant(id, DEV_ITEMS);
        if (state) state = { ...state, items };
        say(TEXT.got(describeItems(DEV_ITEMS)));
        options.onCollected?.();
      });
    });
    const devFactory = el(
      'button',
      {
        type: 'button',
        class: 'hp-button hp-button-secondary hp-button-small',
        'data-testid': 'factory-dev-ready',
      },
      TEXT.devFactory,
    );
    devFactory.addEventListener('click', () => {
      void act(async (id, at) => {
        const res = await fapi.dev.ready(id);
        apply(at, res);
        await refresh();
      });
    });
    sheet.append(dev, devFactory);
  }

  const openBag = () => {
    // Chips still flying land at once rather than cross the Bag.
    summary.landNow();
    sheet.hidden = false;
    bagNew = 0;
    showBagNew();
    render();
    void refresh();
    options.onBagOpen?.();
  };
  open.addEventListener('click', openBag);
  chip.addEventListener('click', openBag);
  const closeBag = (): void => {
    sheet.hidden = true;
    picked = null;
    say('');
    render();
  };
  close.addEventListener('click', closeBag);

  const say = (text: string) => {
    note.textContent = text;
  };

  // ── Server calls ──────────────────────────────────────────────────────

  /** Applies a reply for the map still on screen; drops it otherwise. */
  const apply = (at: number, next: Partial<InventoryResponse> & { now: string }): boolean => {
    if (at !== generation || !state) return false;
    clock.sync(next.now);
    state = { ...state, ...next };
    options.onClockSync?.();
    return true;
  };

  /** When the server last said the next thing finishes (its `nextAt`; gatherers' cycles included). */
  let serverNextAt: string | null = null;
  /** The last settle failed and its one retry is spent (reset by a settle that works). */
  let retried = false;
  /** Bumped by every settle sent, so an older reply can't put an older bag back. */
  let settlesSent = 0;

  /** The Factory's batches that landed, from the tile it stands on, if the map knows it. */
  function factoryFrom(
    landed: readonly Landed[],
    factory: InventoryResponse['factory'],
  ): LandedFrom[] {
    const at = factory ? options.locate?.building?.(factory.buildingId) : null;
    if (!at) return [];
    return landed.filter((l) => l.kind === 'factory').map((l) => ({ ...at, items: l.items }));
  }

  /** The trained squishies with their pictures, as many as are ready in time (#465). */
  async function withPortraits(trained: SettleResponse['trained']): Promise<TrainedSquishy[]> {
    const portraits = options.portraits;
    const pictures = await Promise.all(
      trained.map(async (t) => {
        const species = t.speciesId === undefined ? undefined : SPECIES.get(t.speciesId);
        if (!portraits || !species) return null;
        const look = { species, instanceId: t.squishyId, accessory: t.accessory ?? null };
        let timer: number | undefined;
        const late = new Promise<null>((done) => {
          timer = window.setTimeout(() => {
            done(null);
          }, PORTRAIT_WAIT_MS);
        });
        const picture = await Promise.race([portraits.get(look).catch(() => null), late]);
        window.clearTimeout(timer);
        return picture;
      }),
    );
    return trained.map(({ name, xp }, i) => {
      const portrait = pictures[i];
      return portrait ? { name, xp, portrait } : { name, xp };
    });
  }

  /** The gathers on the go, without banking anything (none if it can't tell). */
  async function gathersGoing(id: string): Promise<readonly Gather[]> {
    try {
      return (await api.get(id)).gathers;
    } catch {
      return [];
    }
  }

  async function refresh(): Promise<void> {
    const id = mapId;
    const at = generation;
    if (!id) return;
    settlesSent += 1;
    const sent = settlesSent;
    try {
      // The gathers going before, so what finished can fly from its tile (#419).
      // A map just opened has no bag yet: one read first (it banks nothing),
      // so the first settle, the one that says "Welcome back!", knows them too.
      const before = state?.gathers ?? (await gathersGoing(id));
      if (at !== generation) return;
      const {
        landed,
        trained,
        cleared = [],
        produced = [],
        nextAt,
        ...fresh
      } = await api.settle(id);
      if (at !== generation) return;
      // A newer settle went out meanwhile: its bag wins, but what this one
      // banked still pops up (the server put it in the bag).
      const newest = sent === settlesSent;
      if (newest) {
        clock.sync(fresh.now);
        state = fresh;
        serverNextAt = nextAt;
        options.onClockSync?.();
      }
      const away = presence.awayMs(id);
      const looks = trained.length > 0 ? await withPortraits(trained) : [];
      if (at !== generation) return;
      if (landed.length > 0 || trained.length > 0 || cleared.length > 0 || produced.length > 0) {
        landings += 1;
        // After a while away, the Factory's things get their own card (#294).
        const card = showsWelcomeBack(landed, away);
        if (card) showWelcome(landed);
        const popped = card ? landed.filter((l) => l.kind !== 'factory') : landed;
        const finished = before.filter((g) => !fresh.gathers.some((f) => f.id === g.id));
        // The card's things are in the Bag too: they count as news. Land
        // that finished clearing (#242) drops its things into the same banner,
        // flying from the cleared tile; the Factory's from its homestead (#461).
        // What my resource buildings made (#601) flies from their homesteads,
        // with "Your Stone Quarry made 96 🪨" under the chips.
        const made = producedLines(produced);
        showLanded(
          [...popped, ...cleared, ...produced],
          looks,
          away >= WELCOME_BACK_MS,
          [...finished, ...cleared, ...produced, ...factoryFrom(popped, fresh.factory)],
          [...landed, ...cleared, ...produced],
          [clearedNote(cleared), ...made].filter((t) => t !== '').join(' '),
          made,
        );
        if (landed.length > 0 || cleared.length > 0 || produced.length > 0) {
          options.onCollected?.();
        }
      }
      // Only while the kid can see it: a tab left open in the background
      // keeps settling, and that isn't being here.
      presence.settled(id, document.visibilityState === 'visible');
      options.onSettled?.();
      if (!newest) return;
      render();
      scheduleSettle();
    } catch (err) {
      if (at !== generation) return;
      say(messageOf(err));
      // Once more in a moment, so a finished thing doesn't sit at "ready!" until
      // the app is reopened; after that, the next open, Bag or return asks.
      if (!retried) {
        retried = true;
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          settleTimer = undefined;
          void refresh();
        }, SETTLE_RETRY_MS);
      }
      return;
    }
    retried = false;
  }

  /**
   * One settle, timed for the next thing to finish: the server's `nextAt`,
   * or a craft or gather started since. Never sooner than the slack, so a
   * clock a few ms apart can't make it ask in a loop.
   */
  function scheduleSettle(): void {
    window.clearTimeout(settleTimer);
    settleTimer = undefined;
    if (!mapId || !state) return;
    const due = [
      ...(serverNextAt ? [serverNextAt] : []),
      ...state.crafts.map((c) => c.readyAt),
      ...state.gathers.map((g) => g.readyAt),
      ...(state.clears ?? []).map((c) => c.readyAt),
      ...(state.factory?.batches.flatMap((b) => (b.nextAt ? [b.nextAt] : [])) ?? []),
    ].map((iso) => clock.msUntil(iso));
    if (due.length === 0) return;
    const wait = Math.min(...due) + SETTLE_SLACK_MS;
    settleTimer = window.setTimeout(() => {
      settleTimer = undefined;
      void refresh();
    }, wait);
  }

  function showWelcome(landed: readonly Landed[]): void {
    welcomeGot.replaceChildren(...welcomeChips(landed).map((c) => el('span', {}, c)));
    const going = state?.factory
      ? batchRows(state.factory, clock.now()).filter((r) => !r.finished)
      : [];
    welcomeStill.replaceChildren(
      ...going.map((r) =>
        el('li', {}, `${r.icon} ${FACTORY_TEXT.stillGoing(r.name, r.done, r.total)}`),
      ),
    );
    welcomeStill.hidden = going.length === 0;
    welcome.hidden = false;
    welcomeOk.focus();
  }

  /** Chips of the last landing that flew from their own tile (the dev hook, for the e2e). */
  let fromTiles = 0;
  /**
   * Where each chip flies from (#419): the tile it came from (a finished
   * gather, cleared land, the Factory's homestead), else my home base
   * (crafts, training), else nowhere (it just appears).
   */
  const flyFrom =
    (locate: NonNullable<InventoryScreenOptions['locate']>, sources: readonly LandedFrom[]) =>
    (entry: SummaryEntry): ScreenPoint | null => {
      const source =
        entry.kind === 'item' ? sources.find((g) => (g.items[entry.id] ?? 0) > 0) : undefined;
      // A tile panned off screen gives way to home base, else nothing flies.
      const centre = (box: ScreenBox | null): ScreenPoint | null => {
        if (!box) return null;
        const at = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
        return onScreen(at, innerWidth, innerHeight) ? at : null;
      };
      const tile = centre(source ? locate.tile(source) : null);
      if (tile) fromTiles += 1;
      return tile ?? centre(locate.home());
    };

  /** Pops up what just landed (#419), and counts it as news until the Bag opens. */
  const showLanded = (
    landed: readonly Pick<Landed, 'items'>[],
    trained: readonly TrainedSquishy[] = [],
    away = false,
    /** Tiles things came from (finished gathers, cleared land), so they fly from there. */
    sources: readonly LandedFrom[] = [],
    /** Everything that went in the Bag, if more than shows (the Factory's own card). */
    inBag: readonly Pick<Landed, 'items'>[] = landed,
    /** A line said first, for screen readers ("Land cleared, ready to build!", #242). */
    note = '',
    /** Lines shown under the chips and on the card (what resource buildings made, #601). */
    lines: readonly string[] = [],
  ) => {
    const entries = summaryEntries(landed, trained);
    if (entries.length > 0) {
      const sentence = [note, summarySentence(landed, trained)].filter((t) => t !== '').join(' ');
      fromTiles = 0;
      summary.show({
        entries,
        sentence,
        away,
        lines,
        // Over the map only: with the Bag or the Factory's card open, it all just appears.
        ...(sheet.hidden && welcome.hidden && options.locate
          ? { from: flyFrom(options.locate, sources) }
          : {}),
      });
    }
    const count = summaryCount(summaryEntries(inBag, []));
    if (sheet.hidden && count > 0) {
      bagNew += count;
      newFor = mapId;
      showBagNew();
    }
  };
  const stopSettling = () => {
    window.clearTimeout(settleTimer);
    settleTimer = undefined;
    serverNextAt = null;
    retried = false;
    summary.hide();
  };

  // Back from another app or a locked screen: things may have finished meanwhile.
  // Going away is remembered, for the welcome-back card (#294).
  document.addEventListener('visibilitychange', () => {
    if (!mapId) return;
    if (document.visibilityState === 'visible') {
      presence.arrived(mapId);
      void refresh();
    } else {
      presence.left(mapId);
    }
  });

  const sendDeps = {
    newKey: newIdempotencyKey,
    wait: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
    retryAfterMs: COMMAND_RETRY_MS,
  };

  /**
   * Runs one command for the map on screen, one at a time. A `CONFLICT`
   * (still cooking, something changed) settles and refetches, so the buttons
   * match the server again.
   */
  /** Resolves to null when it ran, or to the line it said about why not. */
  async function act(
    run: (
      mapId: string,
      at: number,
      send: <T>(command: (key: string) => Promise<T>) => Promise<T | null>,
    ) => Promise<void>,
  ): Promise<string | null> {
    const id = mapId;
    if (!id) return TEXT.noMap;
    if (working) return TEXT.justASec;
    let failure: string | null = null;
    const at = generation;
    working = true;
    render();
    try {
      await run(id, at, (command) => sendCommand(sendDeps, command, () => at === generation));
    } catch (err) {
      failure = messageOf(err);
      if (at === generation) {
        say(failure);
        if (err instanceof ApiRequestError && err.code === 'CONFLICT') await refresh();
      }
    } finally {
      working = false;
      if (at === generation) render();
    }
    return failure;
  }

  const startGather = (tile: PublicTile) =>
    act(async (id, at, send) => {
      const res = await send((key) => api.gather(id, { q: tile.q, r: tile.r }, key));
      if (!res || !state || !apply(at, { now: res.now })) return;
      // A finished gather on this node went in the bag as this one started.
      const banked = state.gathers.find((g) => g.q === tile.q && g.r === tile.r);
      state = {
        ...state,
        gathers: [...state.gathers.filter((g) => g.q !== tile.q || g.r !== tile.r), res.gather],
      };
      say(TEXT.started);
      scheduleSettle();
      if (banked) {
        // The server banked it (the reply has no bag): say so, and read the bag again.
        landings += 1;
        showLanded([banked], [], false, [banked]);
        options.onCollected?.();
        void refresh();
      }
    });

  const startCraft = (recipeId: string) =>
    act(async (id, at, send) => {
      const res = await send((key) => api.craft(id, recipeId, key));
      if (!res || !state || !apply(at, { now: res.now, items: res.items })) return;
      // A finished craft went in the bag first (the server frees the pot), so
      // the one just started is the only one cooking.
      const banked = state.crafts;
      state = { ...state, crafts: [res.craft] };
      say('');
      scheduleSettle();
      if (banked.length > 0) {
        landings += 1;
        showLanded(banked);
        options.onCollected?.();
      }
    });

  /** A Crafting Factory batch (#294): the reply is the whole bag, batches included. */
  const startBatch = (recipeId: string, count: number) =>
    act(async (id, at, send) => {
      const res = await send((key) => fapi.start(id, recipeId, count, key));
      if (!res || !apply(at, res)) return;
      say('');
      scheduleSettle();
    });

  const stopBatch = (batchId: string) =>
    act(async (id, at, send) => {
      const res = await send((key) => fapi.stop(id, batchId, key));
      if (!res || !apply(at, res)) return;
      say('');
      showLanded([{ items: res.refunded }]);
      scheduleSettle();
    });

  /** Reads which recipe book pages are open (for the picker); keeps what it knew on failure. */
  async function loadPages(): Promise<void> {
    try {
      openPages = new Set(await readPages());
      renderFactory();
    } catch {
      // The picker shows what it knew.
    }
  }

  function openFactory(recipeId?: string, reread = true): void {
    if (!state?.factory) {
      // Built a moment ago (#295, on a homestead): read the bag, then open.
      if (reread && mapId) {
        void refresh().then(() => {
          openFactory(recipeId, false);
        });
      }
      return;
    }
    factorySheet.hidden = false;
    renderFactory();
    if (recipeId) factoryPanel.pick(recipeId);
    else factoryPanel.showList();
    void loadPages().then(() => {
      if (recipeId && factoryPanel.mode === 'pick') factoryPanel.pick(recipeId);
    });
  }

  function renderFactory(): void {
    const view = state?.factory ?? null;
    factoryStrip.update(sheet.hidden ? null : view);
    if (factorySheet.hidden || !state || !view) {
      if (!view) factorySheet.hidden = true;
      return;
    }
    factoryPanel.update({ view, items: state.items, seasons: state.seasons });
  }

  // ── Drawing ───────────────────────────────────────────────────────────

  const button = (label: string, onTap: () => void, extra: Record<string, string> = {}) => {
    const b = el(
      'button',
      { type: 'button', class: 'hp-button hp-button-primary bag-action', ...extra },
      label,
    );
    b.disabled = working;
    b.addEventListener('click', onTap);
    return b;
  };

  /**
   * A countdown on screen. Rows and buttons are built only when the data
   * changes; the once-a-second tick only rewrites these texts, so a finger
   * resting on a button never has it swapped out from under it. The words
   * are one Text node kept for the countdown's life and rewritten in place
   * (`data`), never replaced: WebKit pairs a pointer's release with the very
   * node its press landed on, which for a label is its Text node, and fires
   * no click when that node is gone by the release (a `textContent` rewrite
   * swaps it). A trackpad or mouse on an iPad, Safari on a Mac and CI's
   * mouse all lost the gather chip's click whenever a tick fell inside the
   * press; a finger on iOS presses and lifts in one go and never did. When
   * one reaches zero, the screen redraws once (its button appears).
   */
  interface Countdown {
    text: Text;
    readyAt: string;
    say: (left: string) => string;
  }
  let bagCountdowns: Countdown[] = [];
  let tileCountdowns: Countdown[] = [];
  let chipCountdowns: Countdown[] = [];

  const countdown = (
    list: Countdown[],
    tag: 'span' | 'p',
    cls: string,
    readyAt: string,
    say: (left: string) => string,
  ): HTMLElement => {
    const text = document.createTextNode(say(formatTimeLeft(clock.msUntil(readyAt))));
    list.push({ text, readyAt, say });
    return el(tag, { class: cls }, text);
  };

  function renderDetail(item: { id: string; count: number } | null): void {
    if (!item) picked = null;
    detailBox.hidden = item === null;
    if (!item) {
      detailBox.replaceChildren();
      return;
    }
    const d = itemDetail(item.id, item.count);
    detailBox.replaceChildren(
      el(
        'div',
        { class: 'bag-detail-head' },
        el(
          'span',
          { class: 'bag-detail-icon', 'aria-hidden': 'true' },
          itemIconNode(item.id, 'bag-line-icon'),
        ),
        el(
          'span',
          { class: 'bag-detail-title' },
          el('strong', {}, d.name),
          el('span', { class: 'bag-detail-have' }, d.have),
        ),
      ),
      el('p', { class: 'bag-detail-purpose' }, d.purpose),
      el(
        'div',
        { class: 'bag-chips' },
        ...d.chips.map((c) => {
          // A food's "Care: Feed" opens care with it picked (option C, 2026-10-30).
          const feed = c.feed;
          if (feed === undefined || !options.onFeed) {
            return el(
              'span',
              { class: c.battle ? 'bag-chip bag-chip-battle' : 'bag-chip' },
              c.text,
            );
          }
          const button = el(
            'button',
            {
              type: 'button',
              class: 'bag-chip bag-chip-feed',
              'data-testid': 'bag-feed',
              'data-item': feed,
            },
            c.text,
          );
          button.addEventListener('click', () => {
            options.onFeed?.(feed);
          });
          return button;
        }),
      ),
      ...(d.where
        ? [el('p', { class: 'bag-detail-where' }, el('b', {}, TEXT.findIt), ` ${d.where}`)]
        : []),
    );
  }

  /** A tool's durability bar (#199): what's left of the one in hand. */
  function usesBar(share: number): HTMLElement {
    const fill = el('span', { class: 'bag-item-uses-fill' });
    fill.style.width = `${String(Math.round(share * 100))}%`;
    return el('span', { class: 'bag-item-uses', 'aria-hidden': 'true' }, fill);
  }

  function renderBag(): void {
    bagCountdowns = [];
    if (sheet.hidden || !state) return;

    const items = bagItems(state.items);
    itemsBox.replaceChildren(
      ...(items.length === 0
        ? [el('li', { class: 'bag-empty' }, TEXT.empty)]
        : items.map((item) => {
            const tile = el(
              'button',
              {
                type: 'button',
                class: 'bag-item',
                'data-item': item.id,
                'aria-pressed': String(item.id === picked),
              },
              el(
                'span',
                { class: 'bag-item-icon', 'aria-hidden': 'true' },
                itemIconNode(item.id, 'bag-line-icon'),
              ),
              el(
                'span',
                { class: 'bag-item-count' },
                String(item.tool ? item.tool.tools : item.count),
              ),
              el('span', { class: 'bag-item-name' }, item.name),
              // A tool's durability bar: what's left of the one in hand (#199).
              ...(item.tool ? [usesBar(item.tool.share)] : []),
            );
            tile.addEventListener('click', () => {
              picked = picked === item.id ? null : item.id;
              render();
              // The grid was redrawn: keep focus on the tile that was tapped.
              itemsBox.querySelector<HTMLElement>(`[data-item="${item.id}"]`)?.focus();
              // On a phone the card can open below the fold: bring it up.
              if (picked !== null) detailBox.scrollIntoView({ block: 'nearest' });
            });
            return el('li', { class: 'bag-cell' }, tile);
          })),
    );
    renderDetail(items.find((i) => i.id === picked) ?? null);

    gathersTitle.hidden = state.gathers.length === 0;
    gathersBox.replaceChildren(
      ...state.gathers.map((g) => {
        const label = `${itemIcon(g.resource)} ${itemName(g.resource)}`;
        return el(
          'li',
          { class: 'bag-row' },
          el('span', { class: 'bag-row-name' }, label),
          clock.msUntil(g.readyAt) > 0
            ? countdown(bagCountdowns, 'span', 'bag-row-wait', g.readyAt, (left) => left)
            : el('span', { class: 'bag-row-wait' }, TEXT.readyNow),
        );
      }),
    );

    // Every craft on the go, whatever its recipe or season (the recipe book
    // shows the one cooking too); a finished one lands by itself.
    craftsTitle.hidden = state.crafts.length === 0;
    craftsBox.replaceChildren(
      ...bagCrafts(state.crafts).map(({ craft, name, icon }) =>
        el(
          'li',
          { class: 'bag-row', 'data-craft': craft.id },
          el('span', { class: 'bag-row-name' }, `${icon} ${name}`),
          clock.msUntil(craft.readyAt) > 0
            ? countdown(bagCountdowns, 'span', 'bag-row-wait', craft.readyAt, (left) =>
                TEXT.making(name, left),
              )
            : el('span', { class: 'bag-row-wait' }, TEXT.readyNow),
        ),
      ),
    );

    // Only a craft still cooking keeps the pot busy: a finished one is
    // banked as the next one starts (the server frees the pot).
    const cooking = state.crafts.filter((c) => clock.msUntil(c.readyAt) > 0);
    recipesBox.replaceChildren(
      ...bagRecipes(state.items, cooking, state.seasons).map(({ recipe, icon, cost, state: s }) => {
        let action: Node;
        // The Factory (#294): "🏭 Queue" beside Make, once one is built.
        const queue =
          state?.factory && s.kind !== 'sleeping' && s.kind !== 'short'
            ? [
                button(
                  FACTORY_TEXT.queue,
                  () => {
                    openFactory(recipe.id);
                  },
                  { 'data-queue': recipe.id, class: 'hp-button hp-button-secondary bag-action' },
                ),
              ]
            : [];
        if (s.kind === 'ready') {
          action = el(
            'span',
            { class: 'bag-row-actions' },
            button(TEXT.make, () => void startCraft(recipe.id), {
              'data-recipe': recipe.id,
            }),
            ...queue,
          );
        } else if (s.kind === 'busy' && queue.length > 0) {
          action = el(
            'span',
            { class: 'bag-row-actions' },
            el('span', { class: 'bag-row-note' }, TEXT.craftBusy),
            ...queue,
          );
        } else {
          action = el(
            'span',
            { class: 'bag-row-note' },
            s.kind === 'busy' ? TEXT.craftBusy : s.note,
          );
        }
        return el(
          'li',
          { class: 'bag-row', 'data-recipe-row': recipe.id },
          el(
            'span',
            { class: 'bag-row-name' },
            `${icon} ${recipe.name}`,
            el('span', { class: 'bag-row-cost' }, cost),
          ),
          action,
        );
      }),
    );
  }

  function renderTile(): void {
    shownAction = null;
    tileCountdowns = [];
    if (!panel) return;
    const { container, tile } = panel;
    if (!state) {
      container.replaceChildren();
      return;
    }
    const action = tileAction(tile, user?.id ?? null, state, clock.now());
    shownAction = action;
    const line = (text: string) => el('p', { class: 'tile-action-note' }, text);
    switch (action.kind) {
      case 'none':
        container.replaceChildren();
        break;
      case 'gather':
        container.replaceChildren(
          line(TEXT.gives(describeItems(action.yields))),
          button(TEXT.gather, () => void startGather(tile), { 'data-testid': 'tile-gather' }),
        );
        break;
      case 'waiting': {
        const left = countdown(
          tileCountdowns,
          'p',
          'tile-action-note tile-action-waiting',
          action.gather.readyAt,
          TEXT.waiting,
        );
        left.dataset['testid'] = 'tile-gathering';
        container.replaceChildren(left, line(TEXT.keepsGoing));
        break;
      }
      case 'landing': {
        const landing = line(TEXT.landing);
        landing.dataset['testid'] = 'tile-landing';
        container.replaceChildren(line(TEXT.gives(describeItems(action.gather.items))), landing);
        break;
      }
      case 'sleeping':
        container.replaceChildren(line(action.note));
        break;
      case 'clearing': {
        const left =
          clock.msUntil(action.readyAt) > 0
            ? countdown(
                tileCountdowns,
                'p',
                'tile-action-note tile-action-waiting',
                action.readyAt,
                CLEAR_TEXT.clearing,
              )
            : line(CLEAR_TEXT.clearingDone);
        left.dataset['testid'] = 'tile-clearing';
        container.replaceChildren(left);
        break;
      }
      case 'busy':
        container.replaceChildren(
          clock.msUntil(action.readyAt) > 0
            ? countdown(tileCountdowns, 'p', 'tile-action-note', action.readyAt, TEXT.busy)
            : line(TEXT.busyDone),
        );
        break;
    }
  }

  /** The gathering chip, while something's gathering and the Bag is shut. */
  function renderChip(): void {
    chipCountdowns = [];
    chip.disabled = working;
    const shown = mapId !== null && state !== null && sheet.hidden;
    const model = shown && state ? gatherChip(state.gathers, (iso) => clock.msUntil(iso)) : null;
    shownChip = model ? (model.ready ? 'ready' : 'waiting') : null;
    chip.hidden = model === null;
    if (!model) return;
    const { gather } = model;
    const what = `${itemIcon(gather.resource)} ${itemName(gather.resource)}`;
    const more = model.more > 0 ? TEXT.chipMore(model.more) : '';
    chip.classList.toggle('gather-chip-ready', model.ready);
    chip.replaceChildren(
      model.ready
        ? `${TEXT.chipReady(what)}${more}`
        : countdown(
            chipCountdowns,
            'span',
            'gather-chip-text',
            gather.readyAt,
            (left) => `${TEXT.chipWaiting(what, left)}${more}`,
          ),
    );
  }

  function render(): void {
    open.hidden = mapId === null;
    renderBag();
    renderFactory();
    renderTile();
    renderChip();
    syncTicker();
    for (const listener of clearListeners) listener();
  }

  /** One tick: rewrite countdown texts; redraw only when one has finished. */
  function tick(): void {
    let finished = false;
    for (const c of [...bagCountdowns, ...tileCountdowns, ...chipCountdowns]) {
      const left = clock.msUntil(c.readyAt);
      if (left <= 0) finished = true;
      else c.text.data = c.say(formatTimeLeft(left));
    }
    if (finished) render();
  }

  /** Ticks once a second only while a countdown is on screen. */
  function syncTicker(): void {
    const counting = bagCountdowns.length + tileCountdowns.length + chipCountdowns.length > 0;
    if (counting && ticker === undefined) {
      ticker = window.setInterval(tick, 1000);
    } else if (!counting && ticker !== undefined) {
      window.clearInterval(ticker);
      ticker = undefined;
    }
  }

  /** Who to tell when the bag changed (the Build sheet's Clear land card). */
  const clearListeners: (() => void)[] = [];

  /**
   * Clearing land from the Build sheet (#242): Clear land on an empty
   * homestead of mine that a tool can clear, and on a spot, the buildings it
   * makes room for (`clearCards`). The server checks the tool, the tile and
   * what's on it (CLAUDE.md rule 1).
   */
  const clearSource: BuildSource = {
    cards: (tile) => (state ? clearCards(tile, user?.id ?? null, state, clock.now()) : []),
    build: async (card, tile) => {
      // The waiting building cards are only ever shown, never built.
      if (card.id !== CLEAR_CARD_ID) return null;
      const id = mapId;
      if (!id || !state) return null;
      const at = generation;
      try {
        const res = await sendCommand(
          sendDeps,
          (key) => api.clear(id, { q: tile.q, r: tile.r }, key),
          () => at === generation,
        );
        if (!res || !apply(at, { now: res.now, items: res.items })) return null;
        state = { ...state, clears: [...(state.clears ?? []), res.clearing] };
        scheduleSettle();
        render();
        return CLEAR_TEXT.started;
      } catch (err) {
        // Something changed (a tool used up, a clear already going): read it all again.
        if (at === generation && err instanceof ApiRequestError && err.code === 'CONFLICT') {
          void refresh();
        }
        throw err;
      }
    },
    onChange: (listener) => {
      clearListeners.push(listener);
    },
    // Another card built something that took from the bag: read it again.
    invalidate: () => {
      void refresh();
    },
  };

  return {
    clearSource,
    closeBag,
    setMap: async (next) => {
      if (next === mapId) return;
      if (mapId) presence.left(mapId);
      if (next) presence.arrived(next);
      generation += 1;
      mapId = next;
      state = null;
      sheet.hidden = true;
      factorySheet.hidden = true;
      stopSettling();
      if (next !== null && next !== newFor) {
        bagNew = 0;
        newFor = null;
        showBagNew();
      }
      say('');
      render();
      if (next) await refresh();
    },
    setUser: (next) => {
      if (next?.id === user?.id) return;
      user = next;
      generation += 1;
      mapId = null;
      state = null;
      sheet.hidden = true;
      stopSettling();
      bagNew = 0;
      newFor = null;
      showBagNew();
      render();
    },
    get bag() {
      return state;
    },
    refresh,
    craft: (recipeId) => startCraft(recipeId),
    queue: (recipeId, count) => startBatch(recipeId, count),
    stopBatch: (batchId) => stopBatch(batchId),
    openFactory: (recipeId) => {
      openFactory(recipeId);
    },
    gameNow: () => clock.now(),
    syncClock: async (id) => {
      try {
        clock.sync((await api.get(id)).now);
        options.onClockSync?.();
      } catch {
        // The settle that follows the map's opening syncs it.
      }
    },
    isPageOpen: (key) => openPages.has(key),
    msUntil: (iso) => clock.msUntil(iso),
    tileActions: {
      show: (container, tile) => {
        panel = { container, tile };
        render();
      },
      hide: () => {
        panel?.container.replaceChildren();
        panel = null;
        shownAction = null;
        render();
      },
    },
    get debug() {
      if (!mapId || !state) return null;
      return {
        mapId,
        items: { ...state.items },
        gathers: state.gathers.length,
        crafts: state.crafts.length,
        seasons: [...state.seasons],
        bagOpen: !sheet.hidden,
        tileAction: shownAction?.kind ?? null,
        chip: shownChip,
        toast: summary.text,
        summary: summary.debug,
        bagNew,
        landings,
        fromTiles,
        batches: state.factory ? state.factory.batches.length : null,
        factorySheet: factorySheet.hidden ? null : factoryPanel.mode,
        welcome: !welcome.hidden,
      };
    },
  };
}

/** `localStorage`, or null where it throws (private mode, blocked site data). */
function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
